import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { isNumber, uniqueId } from "lodash";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Event, Experience, Permission, Product, ProductReference, Timeslot } from "types";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { allowGuideAndInventoryAssign, getEventOption } from "../../models/Event";
import { getCustomerName } from "../../models/Purchase";
import { getId } from "../../models/Timeslot";
import { useDashboardFilterStore } from "../../stores/dashboardFilterStore";
import { useRosterDrawerStore } from "../../stores/rosterDrawerStore";
import { DashboardMode } from "../../types/Dashboard";
import { fetchTimeslotByEvent } from "../Dashboard/helper";
import { EventCapacity, EventEquipmentList, EventGuideList } from "../Dashboard/Roster/ResourceInformation";
import { ErrorBoundary } from "../ErrorBoundary";
import { BannerArrival, BannerBadges, BannerDemographicList } from "./Banner";
import { EventOptionRow } from "./EventOptionRow";
import { IsGranted } from "./IsGranted";
import { NoTranslate } from "./NoTranslate";
import { ProductImage } from "./ProductImage";

export enum EventBannerMode {
    DEFAULT = "default",
    PRINT = "print",
}

export interface EventBannerProps {
    product: Product | ProductReference;
    timeslot: Timeslot;
    isVirtual: boolean;
    mode?: EventBannerMode;
    innerRef?: React.RefObject<HTMLDivElement>;
}

export const EventBanner = ({
    product,
    timeslot,
    isVirtual,
    innerRef,
    mode = EventBannerMode.DEFAULT,
}: EventBannerProps) => {
    const seller = useSeller();

    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);
    const { data: experience } = useResource<Experience>(["products", timeslot.product]);

    const arrivalDateTime = timeslot.arrivalDatetime;
    const demographics = timeslot.purchasedUnits?.demographics ?? [];

    const title = product.name ?? "";
    const arrivalDate = formatDate(arrivalDateTime, DateFormat.DATE_LONG);
    const arrivalTime = isNumber(timeslot.time) ? formatDate(arrivalDateTime, DateFormat.TIME, false) : null;

    const isManual = event?.manual;
    const isPrivate = !!timeslot.customerName ?? false;
    const customerName = timeslot.customerName ? getCustomerName(timeslot) : "";

    const currentOption = getEventOption(event, experience);
    const popoverParentId = uniqueId("popoverParentId");

    const isPrintMode = mode === EventBannerMode.PRINT;

    const history = useHistory();
    const { filters } = useDashboardFilterStore((state) => state);
    const isListMode = filters.mode === DashboardMode.LIST;
    const isCalendarMode = [DashboardMode.DAY, DashboardMode.MONTH, DashboardMode.WEEK].includes(filters.mode);
    const urlParams = useParams<{ timeslotId?: string }>();
    const setDrawerTimeslot = useRosterDrawerStore((state) => state.setTimeslot);

    const handleDashboardRoster = async (eventId: ID, shouldRedirectToDashboard = false) => {
        const newId = getId({
            date: timeslot.date,
            time: timeslot.time,
            product: timeslot.product,
            event: { id: eventId },
        });
        if (isListMode && !!urlParams.timeslotId) {
            const link = shouldRedirectToDashboard ? "/dashboard" : `/dashboard/${newId}`;
            history.push(link);
        } else if (isCalendarMode) {
            // In case we applied filters in dashboard and after update, the timeslot doesn't exists
            // in current filter view, we still need to show the timeslot in drawer. so we fetch the
            // updated timeslot and set it in drawer store.
            let updatedTimeslot: Timeslot = { ...timeslot, event: { id: eventId } };
            setDrawerTimeslot(updatedTimeslot);
            try {
                updatedTimeslot = await fetchTimeslotByEvent(eventId);
                setDrawerTimeslot(updatedTimeslot);
            } catch (error) {
                console.log("Error in fetching timeslot by event id:", eventId, error);
            }
        }
    };

    return (
        <ErrorBoundary fallbackComponent={<BannerFallback timeslot={timeslot} />}>
            <div ref={innerRef} id={popoverParentId} className="flex space-x-4">
                <div className="min-w-min flex-shrink-0">
                    <ProductImage
                        product={product}
                        size={isPrintMode ? "medium" : "giant"}
                        className={clsx({ "mr-0": isPrintMode })}
                        classNames={{ image: clsx({ "h-20 w-20": isPrintMode }) }}
                    />
                </div>

                <div className="flex-grow space-y-2">
                    <div className="space-y-1">
                        <NoTranslate
                            as="div"
                            className="text-xl font-bold leading-7 line-clamp-2"
                            data-testid="banner-product-name"
                        >
                            {title}
                        </NoTranslate>
                        <BannerArrival
                            arrivalDate={arrivalDate}
                            arrivalTime={arrivalTime}
                            event={event}
                            experience={experience}
                        />
                        {experience && isPrintMode && (
                            <div>
                                <BannerDemographicList demographics={demographics} experience={experience} />
                            </div>
                        )}
                    </div>
                    <BannerBadges
                        customerName={customerName}
                        isPrivate={isPrivate}
                        isVirtual={isVirtual}
                        isManual={isManual}
                        event={event}
                    />
                    {event && experience && !isPrintMode && (
                        <EventOptionRow event={event} experience={experience} option={currentOption} />
                    )}
                </div>
                <div className="flex shrink-0 pt-2 pr-3">
                    <div className="flex w-full justify-end lg:space-x-2 xl:space-x-6">
                        {allowGuideAndInventoryAssign(seller, event, timeslot) ? (
                            <>
                                <EventGuideList
                                    event={event}
                                    timeslot={timeslot}
                                    popoverParentId={popoverParentId}
                                    editable={!isPrintMode}
                                    handleDashboardRoster={handleDashboardRoster}
                                />

                                <IsGranted permissions={Permission.VIEW_RESOURCES_EQUIPMENT}>
                                    <EventEquipmentList
                                        event={event}
                                        timeslot={timeslot}
                                        editable={!isPrintMode}
                                        handleDashboardRoster={handleDashboardRoster}
                                    />
                                </IsGranted>
                            </>
                        ) : null}
                        <EventCapacity
                            timeslot={timeslot}
                            editable={!isPrintMode}
                            handleDashboardRoster={handleDashboardRoster}
                            event={event}
                            experience={experience}
                        />
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export const BannerFallback = ({ timeslot }: { timeslot: Timeslot }) => {
    return (
        <div className="flex h-40 items-center justify-between rounded-lg bg-gray-dark px-8 text-white">
            Error rendering banner for timeslot <var data-var="timeslotId">{getId(timeslot)}</var>
        </div>
    );
};
